import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

/**
 * TextRevealMatrix — fades words in one-by-one as the element scrolls
 * into view. Each word starts at low opacity + slight y offset and
 * resolves to its final position with the cubic-bezier(0.16,1,0.3,1) spring.
 *
 * @param {object} props
 * @param {string} props.text  Text to reveal word-by-word.
 * @param {string} [props.className]  Extra classes for the wrapper.
 * @param {string} [props.as]  Wrapper element (default "p").
 */
export default function TextRevealMatrix({ text, className = "", as: Tag = "p" }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 0.85", "start 0.25"],
  });
  const words = String(text).split(" ");

  return (
    <Tag
      ref={ref}
      className={className}
      data-testid="text-reveal-matrix"
      style={{ display: "inline" }}
    >
      {words.map((w, i) => (
        <RevealWord
          key={i}
          word={w + (i < words.length - 1 ? " " : "")}
          index={i}
          total={words.length}
          scrollProgress={scrollYProgress}
        />
      ))}
    </Tag>
  );
}

function RevealWord({ word, index, total, scrollProgress }) {
  const start = index / total;
  const end = (index + 1) / total;
  const opacity = useTransform(scrollProgress, [start, end], [0.18, 1]);
  const y = useTransform(scrollProgress, [start, end], [10, 0]);
  return (
    <motion.span
      style={{ opacity, y, display: "inline-block", whiteSpace: "pre" }}
    >
      {word}
    </motion.span>
  );
}
